import { ReactRenderer } from '@tiptap/react';
import { MentionList } from './MentionList';
import { Mention } from '@tiptap/extension-mention';
import tippy from 'tippy.js';
import _ from 'lodash';
import { searchUsers } from '~/monolith-api';
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';

export interface User {
   userid: number;
   unique_username: string;
   username: string;
   rank?: number;
   image: {
      thumbnail: string;
   };
}

const MAX_USERS_SUGGESTED = 5;
// This should always be set to a lower than legitimate rank
const DEFAULT_RANK = 6;

type GetUserMentionOptions = {
   query: string;
};

async function getUserMentionDetails({ query }: GetUserMentionOptions) {
   const userlist: User[] = [];

   // If no search query, show the list of users participating in the
   // disussions on the page.
   if (!query && userlist) {
      // Grab the user list from the page
      return userlist.slice(0, MAX_USERS_SUGGESTED);
   }

   /**
    * Rank users for sorting displayed suggestions.
    * User ranking map (1 is highest rank):
    * 1. unique_username exact match
    * 2. username exact match
    * 3. unique_username start of string match
    * 4. username start of string match
    * 5. username or unique_username substring match
    */

   let matchedLocalUsers = userlist
      .map((u: User) => {
         let rank = DEFAULT_RANK; // Default rank to lowest value
         if (u.unique_username === query) {
            rank = 1;
         } else if (u.username === query) {
            rank = 2;
         } else if (u.unique_username.startsWith(query)) {
            rank = 3;
         } else if (u.username.startsWith(query)) {
            rank = 4;
         } else if (u.unique_username.includes(query) || u.username.includes(query)) {
            rank = 5;
         }

         return { ...u, rank: rank };
      })
      .filter((u: User) => (u.rank ?? 0) < DEFAULT_RANK)
      // Sort matched users based on how well they matches query (i.e. rank)
      .sort((u1: User, u2: User) => (u1.rank ?? 0) - (u2.rank ?? 0))
      // Drop the rank field after its use in sorting
      .map((u: User) => _.omit(u, ['rank']));

   // If there are more than MAX_USERS_SUGGESTED local users that match the
   // query, show that list and don't make a time-consuming request.
   if (matchedLocalUsers.length >= MAX_USERS_SUGGESTED) {
      return matchedLocalUsers.slice(0, MAX_USERS_SUGGESTED);
   }

   // If there is a search query this api request will fire. Note this
   // request only returns username, unique_username, and mini version
   // of their avatar image
   const { results: users } = await searchUsers(query, {
      limit: MAX_USERS_SUGGESTED,
      uniqueOnly: true,
   });
   return users;
}

function renderMentions() {
   let reactRenderer: ReactRenderer<any>;
   let popup: ReturnType<typeof tippy>;

   return {
      onStart: (props: SuggestionProps<User>) => {
         reactRenderer = new ReactRenderer(MentionList, {
            props,
            editor: props.editor,
         });

         popup = tippy('body', {
            getReferenceClientRect: props.clientRect as () => ClientRect,
            appendTo: () => document.body,
            content: reactRenderer.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
         });
      },
      onUpdate(props: SuggestionProps<User>) {
         reactRenderer.updateProps(props);

         popup[0].setProps({
            getReferenceClientRect: props.clientRect as () => ClientRect,
         });
      },
      onKeyDown(props: SuggestionKeyDownProps) {
         if (props.event.key === 'Escape') {
            popup[0].hide();

            return true;
         }

         return reactRenderer.ref?.onKeyDown(props);
      },
      onExit() {
         popup[0].destroy();
         reactRenderer.destroy();
      },
   };
}

export const CustomMention = Mention.configure({
   HTMLAttributes: {
      class: 'mention',
   },
   suggestion: {
      items: getUserMentionDetails,
      render: renderMentions,
      // Modified from @tiptap/extension-mention
      command: ({ editor, range, props }) => {
         // increase range.to by one when the next node is of type "text"
         // and starts with a space character
         const nodeAfter = editor.view.state.selection.$to.nodeAfter;
         const overrideSpace = nodeAfter?.text?.startsWith(' ');

         if (overrideSpace) {
            range.to += 1;
         }

         editor
            .chain()
            .focus()
            .insertContentAt(range, [
               {
                  type: 'mention',
                  attrs: props,
               },
               {
                  type: 'text',
                  text: ' ',
               },
            ])
            .run();

         // Needed to fix the browser's selection after clicking on a suggestion
         window.getSelection()?.collapseToEnd();
      },
   },
});
